import {
  Box,
  TableFooter,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Avatar,
  Divider,
} from '@mui/material';
import styled from '@emotion/styled';
// Components
import { OnHoverToolTip, TokenAmount } from '@/common/index';
// Utils
import { toDollar } from '@/utils/index';
// Styles
import { colors } from '@/theme';

const gasHoverText =
  'Transactions require a small gas fee in order to be executed on the blockchain. Gas fees are estimated based on network activity and updated automatically every 30s.';

const StyledRewardsRow = styled(TableRow)({
  '& .muitablecell-root': {
    borderBottom: 'none',
  },
});
const StyledFooterRow = styled(TableRow)({
  '& .MuiTableCell-root': {
    fontWeight: 'bolder',
    fontSize: '16px',
    borderTop: `1px solid ${colors.grey[300]}`,
    borderBottom: 'none',
  },
});

const OrderSummaryTable = ({
  couponBalance,
  couponTitleAndQty,
  currency,
  gasFee,
  gasFeeLoading,
  gasFeePrecision,
  gasFeeToken,
  handleConvertUsdToCoin,
  isCC,
  displayPrecision,
  isExactGas,
  items,
  showGasFeeRow,
  showCouponRow,
  totals,
}) => {
  const { subtotalUsd, totalUsd, totalQty, totalCoin, subtotalCoin } = totals;
  const borderStyle = showCouponRow ? { border: 'none' } : {};

  return (
    <TableContainer>
      <Table aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography variant="h4">Product Details</Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="h4">Quantity</Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="h4">Price</Typography>
            </TableCell>
            {!isCC && (
              <TableCell align="right" sx={{ width: '180px' }}>
                <Typography variant="h4">Currency</Typography>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.productName}>
              <TableCell
                component="th"
                scope="row"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <Avatar
                  alt={item.productName}
                  src={item.productImage}
                  sx={{ marginRight: '14px' }}
                />
                <Typography variant="h5" color={item.noLongerAvailable && 'error'}>
                  {item.productName}
                </Typography>
              </TableCell>
              <TableCell
                align="right"
                sx={{ color: item.noLongerAvailable && colors.error.main }}
              >{`Qty ${item.quantity}`}</TableCell>
              <TableCell
                align="right"
                sx={{ color: item.noLongerAvailable && colors.error.main }}
              >
                {toDollar(item.subtotal)}
              </TableCell>
              {!isCC && (
                <TableCell
                  align="right"
                  sx={{ color: item.noLongerAvailable && colors.error.main }}
                >
                  {
                    <TokenAmount
                      amount={handleConvertUsdToCoin(item.subtotal)}
                      token={currency}
                      align="flex-end"
                      precision={displayPrecision}
                    />
                  }
                </TableCell>
              )}
            </TableRow>
          ))}
          {/* ---------------------- Subtotal ---------------------- */}
          <TableRow>
            <TableCell colSpan={2} sx={{ borderBottom: 'none' }}>
              <Typography variant="h5">Subtotal</Typography>
            </TableCell>
            <TableCell align="right" sx={{ borderBottom: 'none' }}>
              <Typography variant="h5">{toDollar(subtotalUsd)}</Typography>
            </TableCell>
            {!isCC && (
              <TableCell align="right" sx={{ borderBottom: 'none' }}>
                <Typography variant="h5">
                  <TokenAmount
                    amount={subtotalCoin}
                    token={currency}
                    align="flex-end"
                    precision={displayPrecision}
                  />
                </Typography>
              </TableCell>
            )}
          </TableRow>
          {/* ---------------------- Gas Fee ---------------------- */}
          {showGasFeeRow && (
            <TableRow>
              <TableCell colSpan={2} sx={borderStyle}>
                <Box sx={{ display: 'flex' }}>
                  <Typography
                    component={'div'}
                    sx={{ fontWeight: 'bold', marginRight: '10px' }}
                  >
                    {isExactGas ? 'Gas Fee' : 'Est Gas Fee'}
                  </Typography>
                  <OnHoverToolTip hoverText={gasHoverText} width={300} />
                </Box>
              </TableCell>
              {gasFeeLoading ? (
                <TableCell colSpan={2} align="right" sx={borderStyle}>
                  Loading...
                </TableCell>
              ) : (
                <>
                  <TableCell align="right" sx={borderStyle}>
                    {toDollar(gasFee?.usd)}
                  </TableCell>
                  <TableCell align="right" sx={borderStyle}>
                    <TokenAmount
                      amount={gasFee?.coin}
                      token={gasFeeToken}
                      align="flex-end"
                      precision={gasFeePrecision}
                    />
                  </TableCell>
                </>
              )}
            </TableRow>
          )}
          {/* ---------------------- Coupon ---------------------- */}
          {showCouponRow && (
            <>
              <StyledRewardsRow>
                <TableCell colSpan={2}>
                  <Box sx={{ display: 'flex' }}>
                    <Typography variant="h5">{couponTitleAndQty}</Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">{`-${toDollar(couponBalance)}`}</TableCell>
                <TableCell align="right">
                  {!isCC && (
                    <TokenAmount
                      amount={handleConvertUsdToCoin(couponBalance)}
                      token={currency}
                      isNegative
                      align="flex-end"
                      precision={displayPrecision}
                    />
                  )}
                </TableCell>
              </StyledRewardsRow>
              <Divider variant="fullWidth" component="tr" sx={{ height: '16px' }} />
            </>
          )}
        </TableBody>
        {/* ---------------------- Total ---------------------- */}
        <TableFooter>
          <StyledFooterRow>
            <TableCell>
              <Typography variant="h4">Total</Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="h5">Qty {totalQty}</Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="h5">{toDollar(totalUsd)}</Typography>
            </TableCell>
            {!isCC && (
              <TableCell align="right">
                <Typography variant="h5">
                  <TokenAmount
                    amount={totalCoin}
                    token={currency}
                    align="flex-end"
                    precision={displayPrecision}
                  />
                </Typography>
              </TableCell>
            )}
          </StyledFooterRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default OrderSummaryTable;
